import React, { useState } from 'react';
import { retainedEarningsApi } from '../services/api';
import '../styles/Modal.css';

/**
 * Modal for calculating retained earnings adjustment (Schedule III/IndAS)
 *
 * User provides fiscal year end date:
 * - Fiscal Year End: Must be 31 March (e.g., 2025-03-31)
 * - System automatically calculates stub period (Jan-Mar of previous year)
 */
function CalculateREModal({ entity, onClose, onComplete }) {
  const [fiscalYearEnd, setFiscalYearEnd] = useState('');

  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleCalculate = async () => {
    // Validation
    if (!fiscalYearEnd) {
      setError('Please enter fiscal year end date');
      return;
    }

    // Check if it ends on 31 March
    const fyEndDate = new Date(fiscalYearEnd);
    if (fyEndDate.getMonth() !== 2 || fyEndDate.getDate() !== 31) {
      setError('Fiscal year end must be 31 March (e.g., 2025-03-31)');
      return;
    }

    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      const data = await retainedEarningsApi.calculateAdjustment(
        entity.name,
        fiscalYearEnd
      );

      setResult(data);

      // Auto-close modal after showing success for 2 seconds
      if (data.success && onComplete) {
        setTimeout(() => {
          onComplete(data);
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Error calculating retained earnings adjustment:', err);
      setError(
        err.response?.data?.error ||
        err.message ||
        'Failed to calculate retained earnings adjustment'
      );
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.storage) return;

    try {
      const blob = await retainedEarningsApi.downloadWorkbook(
        entity.name,
        result.storage.period,
        result.storage.timestamp
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retained_earnings_${entity.name}_${result.storage.period}_${result.storage.timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading workbook:', err);
      setError('Failed to download workbook');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Calculate Retained Earnings Adjustment</h3>
          <button onClick={onClose} className="modal-close">×</button>
        </div>

        <div className="modal-body">
          {/* Info Alert */}
          <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
            <strong>Schedule III & IndAS Compliant Algorithm:</strong>
            <ol style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', marginBottom: 0 }}>
              <li>Calculate stub period automatically (Jan-Mar of previous year)</li>
              <li>Fetch TB for fiscal year (with S3 caching)</li>
              <li>Use Claude AI to identify all P&L accounts</li>
              <li>Calculate adjustment as sum of P&L opening balances</li>
              <li>Generate Excel with 4 tabs (TB Dump, RE Summary, Journal Entry, P&L Balances)</li>
            </ol>
          </div>

          {/* Fiscal Year End Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label>Fiscal Year End Date (must be 31 March)</label>
              <input
                type="date"
                value={fiscalYearEnd}
                onChange={(e) => setFiscalYearEnd(e.target.value)}
                disabled={calculating}
                placeholder="YYYY-03-31"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                Example: 2025-03-31 (System will automatically calculate stub period: Jan 2024 - Mar 2024)
              </small>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {/* Result Display */}
          {result && result.success && (
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                Calculation Complete!
              </h4>

              {result.status === 'calculated' && (
                <>
                  <p style={{ margin: '0.5rem 0' }}>
                    <strong>Adjustment Value:</strong> {result.adjustment_value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>

                  {result.details && (
                    <>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                        <strong>Fiscal Year:</strong> {result.details.fiscal_year?.start_date} to {result.details.fiscal_year?.end_date}
                      </p>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                        <strong>Stub Period:</strong> {result.details.stub_period?.start_date} to {result.details.stub_period?.end_date}
                      </p>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                        <strong>P&L Accounts:</strong> {result.details.pl_accounts?.total_accounts} accounts identified
                      </p>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', fontStyle: 'italic' }}>
                        {result.details.calculation}
                      </p>
                    </>
                  )}
                </>
              )}

              {result.status === 'invalid_fiscal_year_end' && (
                <p style={{ margin: '0.5rem 0' }}>
                  {result.message} - Adjustment value is 0.
                </p>
              )}

              {result.status === 'not_applicable' && (
                <p style={{ margin: '0.5rem 0' }}>
                  {result.message}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary" disabled={calculating}>
            Close
          </button>

          {result?.storage && (
            <button onClick={handleDownload} className="btn btn-primary">
              Download Workbook
            </button>
          )}

          {!result && (
            <button
              onClick={handleCalculate}
              className="btn btn-primary"
              disabled={calculating}
            >
              {calculating ? 'Calculating...' : 'Calculate Adjustment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CalculateREModal;
